import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PATHS } from "@/constants/testIds";
import { toast } from "sonner";
import { CheckCircle2, Circle, PlayCircle, Award, ArrowLeft, Sparkles } from "lucide-react";
import CertificateCelebrationModal from "@/components/CertificateCelebrationModal";

export default function PathDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [path, setPath] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [celebrationCert, setCelebrationCert] = useState(null);

  const load = async () => {
    const [p, pr] = await Promise.all([
      api.get(`/paths/${slug}`),
      api.get(`/paths/${slug}/progress`),
    ]);
    setPath(p.data);
    setProgress(pr.data);
  };

  useEffect(() => { load(); }, [slug]);

  const enroll = async () => {
    setBusy(true);
    try {
      await api.post(`/paths/${slug}/enroll`);
      toast.success("You're enrolled!");
      await load();
    } finally { setBusy(false); }
  };

  const generateCert = async () => {
    try {
      const { data } = await api.post(`/certificates/generate/${slug}`);
      toast.success("Certificate issued!");
      setCelebrationCert(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot issue certificate yet");
    }
  };

  if (!path) return <div className="label-mono animate-pulse">Loading…</div>;

  const completedSet = new Set(progress?.completed_lessons || []);
  const total = path.lessons?.length || 0;
  const done = completedSet.size;
  const percent = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="space-y-8 animate-fade-up">
      <Link to="/app/paths" className="inline-flex items-center gap-2 text-xs font-mono text-muted-foreground hover:text-primary">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to paths
      </Link>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-8">
          <div className="flex items-start gap-4">
            <div
              className="grid h-14 w-14 place-items-center rounded-xl font-display text-2xl font-black"
              style={{ backgroundColor: `${path.color}22`, color: path.color, border: `1px solid ${path.color}55` }}
            >
              {path.title[0]}
            </div>
            <div className="min-w-0">
              <div className="label-mono">{path.category} · {path.level}</div>
              <h1 className="mt-1 font-display text-3xl font-black tracking-tighter sm:text-4xl">{path.title}</h1>
            </div>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">{path.description}</p>

          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between">
              <div className="label-mono">Your progress</div>
              <div className="font-mono text-xs">{done}/{total} · {percent}%</div>
            </div>
            <Progress value={percent} className="h-2" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {progress?.completed_lessons ? null : (
              <Button data-testid={PATHS.detailEnrollBtn} onClick={enroll} disabled={busy} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">
                Enroll now
              </Button>
            )}
            <Button
              onClick={() => navigate(`/app/paths/${slug}/assessment`)}
              variant="outline"
              className="rounded-full font-mono text-xs uppercase tracking-widest"
            >
              <Sparkles className="mr-2 h-4 w-4" /> Take AI Assessment
            </Button>
            {percent === 100 && (
              <Button onClick={generateCert} variant="outline" className="rounded-full font-mono text-xs uppercase tracking-widest">
                <Award className="mr-2 h-4 w-4" /> Claim certificate
              </Button>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="label-mono">// what you'll get</div>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-start gap-2"><PlayCircle className="mt-0.5 h-4 w-4 text-primary" /> Personalised lessons with real examples</li>
            <li className="flex items-start gap-2"><PlayCircle className="mt-0.5 h-4 w-4 text-primary" /> Adaptive quizzes after every lesson</li>
            <li className="flex items-start gap-2"><PlayCircle className="mt-0.5 h-4 w-4 text-primary" /> Talk to your mentor at any time</li>
            <li className="flex items-start gap-2"><PlayCircle className="mt-0.5 h-4 w-4 text-primary" /> Certificate on completion</li>
          </ul>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="label-mono mb-4">// curriculum</div>
        <ol className="space-y-2">
          {(path.lessons || []).map((title, i) => {
            const isDone = completedSet.has(i);
            return (
              <li key={i}>
                <Link
                  data-testid={PATHS.lessonLink(i)}
                  to={`/app/paths/${slug}/lessons/${i}`}
                  className="group flex items-center gap-4 rounded-xl border border-border p-4 transition hover:border-primary/50"
                >
                  <div className="label-mono w-8 tabular-nums">{String(i + 1).padStart(2, "0")}</div>
                  {isDone ? <CheckCircle2 className="h-5 w-5 text-accent" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
                  <div className="flex-1 font-medium">{title}</div>
                  <div className="label-mono opacity-0 transition group-hover:opacity-100">open →</div>
                </Link>
              </li>
            );
          })}
        </ol>
      </div>
      <CertificateCelebrationModal cert={celebrationCert} onClose={() => setCelebrationCert(null)} />
    </div>
  );
}
